import type { StrokeStepVerdict } from "../handwriting/stroke-step.js";
import type { HandwritingReading } from "../fuxi/handwriting-verdict.js";
import {
  type DrillProgress,
  isFinished,
  stages,
} from "../handwriting/xiezi-stages.js";

/**
 * What the drill says to a reader, in the words the entry page uses.
 *
 * The entry page labels its figure in English with the character in it
 * ("Write 海"), and the drill says what it has to say the same way. Short,
 * because a line under a square a reader is writing in is read between
 * strokes.
 *
 * The line over the square saying where the reader is has a module of its
 * own, `xiezi-stage-words.ts`.
 *
 * **A character written from memory is marked as a 复习 card marks it**, with
 * Correct 对 or Incorrect 不对 first (`src/user/fuxi-verdict.ts`). A slip on
 * one stroke during the earlier stages takes no verdict, since it is help
 * while the character is being drawn. The lines were rewritten with Codex on
 * 2026-10-15, after Hugh found the first wording stilted. A marked line names
 * no character either, since the reader writes the next pass straight under
 * it.
 */

/**
 * The line over the square once every character of an Exercise is written.
 *
 * @param characters - How many characters the Exercise has.
 * @returns The line.
 */
export function exerciseLine(characters: number): string {
  return `Well done, you've completed this exercise and practised ${String(characters)} ${characters === 1 ? "character" : "characters"}.`;
}

/**
 * What follows the finished line once the finish has handed the Exercise's
 * material to 复习, on the handler's page alone.
 */
export const handedOverLine = "These characters are now in your Review.";

/**
 * Where one character of an Exercise is, for the row of characters over it.
 *
 * @param progress - Where the reader is with the character.
 * @returns Its stage, or that it is done.
 */
export function characterPlace(progress: DrillProgress): string {
  return isFinished(progress)
    ? "done"
    : `stage ${String(progress.stage + 1)} of ${String(stages.length)}`;
}

/** An ordinal for a stroke, counting from one as a reader does. */
function ordinal(stroke: number): string {
  const number = stroke + 1;
  const tens = number % 100;
  const ones = number % 10;

  if (tens >= 11 && tens <= 13) {
    return `${String(number)}th`;
  }

  return `${String(number)}${ones === 1 ? "st" : ones === 2 ? "nd" : ones === 3 ? "rd" : "th"}`;
}

/**
 * What to say about a stroke that was not taken, in the first three stages.
 *
 * Nothing is said about a stroke taken. The stroke snapping into place is the
 * whole of the answer, and a word beside it would be read instead of the next
 * stroke.
 *
 * @param verdict - What the stroke was a go at.
 * @param next - The stroke that was wanted.
 * @returns The line, or the empty string for a stroke taken.
 */
export function strokeLine(verdict: StrokeStepVerdict, next: number): string {
  if (verdict.kind === "order") {
    return `You drew the ${ordinal(verdict.stroke)} stroke. Draw the ${ordinal(next)} stroke now.`;
  }

  if (verdict.kind === "wrong") {
    return `That stroke isn't in this character. Try the ${ordinal(next)} stroke again.`;
  }

  return {
    right: "",
    backwards: "Right stroke, wrong direction. Start it from the other end.",
    place: "Right stroke, wrong place. Draw it where the outline shows it.",
  }[verdict.kind];
}

/**
 * What to say about a character written from memory, once it is marked.
 *
 * No line names the character asked for. The reader writes the next pass
 * from memory straight under it.
 *
 * @param reading - What the site made of the ink.
 * @param passed - Whether the pass was passed, which a near neighbour ranked
 * first does not stop. See `src/fuxi/drill-handwriting.ts`.
 * @returns The line.
 */
export function markedLine(
  reading: HandwritingReading,
  passed: boolean,
): string {
  if (passed) {
    return reading.verdict === "another" && reading.looksLike !== undefined
      ? `Correct 对, though it also looks like ${reading.looksLike}.`
      : "Correct 对";
  }

  const another =
    reading.looksLike === undefined
      ? "Incorrect 不对. That isn't the character asked for."
      : `Incorrect 不对. That looks like ${reading.looksLike}.`;

  return {
    written: "Correct 对",
    outOfOrder: "Incorrect 不对. The strokes are out of order.",
    another,
    wrongCount: `Incorrect 不对. It has ${String(reading.expected)} strokes, and you drew ${String(reading.drawn)}.`,
    blank: "Write the character first.",
    unrecognised: "We can't check this character here.",
  }[reading.verdict];
}

/**
 * What a miss cost, after the marked line.
 *
 * @param before - Where the reader was when they wrote it.
 * @param after - Where the miss left them.
 * @returns The line.
 */
export function setbackLine(
  before: DrillProgress,
  after: DrillProgress,
): string {
  return after.stage < before.stage
    ? `Back to stage ${String(after.stage + 1)}.`
    : "One pass back.";
}
